import io from 'socket.io-client';

const LOCAL_SOCKET_URL = 'http://localhost:5001';
const LIVE_SOCKET_URL = 'https://backend.itracknet.com';
const DEFAULT_SOCKET_URL =
  LIVE_SOCKET_URL;
console.log('[newsocketService] using socket URL:', DEFAULT_SOCKET_URL);
const DEFAULT_SOCKET_PATH = process.env.REACT_APP_SOCKET_PATH || '/socket.io';

const readLocalStorage = key => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const readStoredUserObject = () => {
  const rawUser = readLocalStorage('user');

  if (!rawUser) {
    return null;
  }

  try {
    return JSON.parse(rawUser);
  } catch (error) {
    return null;
  }
};

const logSocketEvent = (phase, event, payload) => {
  console.log(`[newsocketService] ${phase}`, {
    event,
    payload,
  });
};

class SocketService {
  constructor() {
    this.socket = null;
    this.initializedSocket = false;
    this.globalListeners = new Map();
    this.connectingPromise = null;
    this.lastConnectionConfig = null;
  }

  getConnectionConfig(userId, state, options = {}) {
    const storedUser = readStoredUserObject();
    const resolvedUserId =
      userId ||
      options.userId ||
      readLocalStorage('userId') ||
      storedUser?.userId ||
      null;
    const resolvedState =
      state || options.state || readLocalStorage('state') || storedUser?.state || null;
    const resolvedIsSuperAdmin =
      options.isSuperAdmin ??
      (readLocalStorage('superAdmin') === 'true' ||
        readLocalStorage('isSuperAdmin') === 'true' ||
        Boolean(storedUser?.superAdmin) ||
        Boolean(storedUser?.isSuperAdmin));
    const resolvedWeb = options.web ?? 'true';

    return {
      userId: resolvedUserId,
      state: resolvedState,
      isSuperAdmin: resolvedIsSuperAdmin,
      web: resolvedWeb,
    };
  }

  attachListeners() {
    if (!this.socket) {
      return;
    }

    for (const [event, callbacks] of this.globalListeners.entries()) {
      this.socket.off(event);
      for (const callback of callbacks) {
        this.socket.on(event, callback);
      }
    }
  }

  initializeSocket(userId = null, state = null, options = {}) {
    const connectionConfig = this.getConnectionConfig(userId, state, options);

    if (!connectionConfig.userId) {
      console.log('[newsocketService] skipping socket connect because userId is missing', {
        defaultUrl: DEFAULT_SOCKET_URL,
        defaultPath: DEFAULT_SOCKET_PATH,
      });
      return Promise.reject(
        new Error('User ID is required to establish a socket connection.'),
      );
    }

    if (this.socket?.connected) {
      return Promise.resolve(this.socket);
    }

    if (this.connectingPromise) {
      return this.connectingPromise;
    }

    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.initializedSocket = false;
    }

    this.lastConnectionConfig = connectionConfig;
    console.log('[newsocketService] connecting', {
      url: DEFAULT_SOCKET_URL,
      path: DEFAULT_SOCKET_PATH,
      query: connectionConfig,
    });

    this.connectingPromise = new Promise((resolve, reject) => {
      this.socket = io(DEFAULT_SOCKET_URL, {
        transports: ['polling'],
        timeout: 20000,
        path: DEFAULT_SOCKET_PATH,
        query: connectionConfig,
      });

      const cleanup = () => {
        if (!this.socket) {
          return;
        }

        this.socket.off('connect', onConnect);
        this.socket.off('connect_error', onConnectError);
      };

      const onConnect = () => {
        this.initializedSocket = true;
        console.log('[newsocketService] connected', {
          id: this.socket?.id,
          query: connectionConfig,
        });
        try {
          this.socket.emit('joinRoom', 'superadmin');
          this.socket.emit('joinRoom', 'isSuperAdmin');
          this.socket.emit('joinRoom', 'web');
          if (connectionConfig.state) {
            this.socket.emit('joinRoom', connectionConfig.state);
          }
        } catch (_) {}
        this.attachListeners();
        cleanup();
        this.connectingPromise = null;
        resolve(this.socket);
      };

      // const onConnectError = err => {
      //   console.log('[newsocketService] connect_error', err);
      //   cleanup();
      //   this.connectingPromise = null;
      //   reject(err);
      // };
      const onConnectError = err => {
        console.log("CONNECT ERROR");
        console.log("MESSAGE:", err?.message);
        console.log("DESCRIPTION:", err?.description);
        console.log("CONTEXT:", err?.context);
        console.log("STACK:", err);
      };
      this.socket.on('connect', onConnect);
      this.socket.on('disconnect', () => {
        this.initializedSocket = false;
        console.log('[newsocketService] disconnected');
      });
      this.socket.on('connect_error', onConnectError);
    });

    return this.connectingPromise;
  }

  connect(userId, state, options = {}) {
    return this.initializeSocket(userId, state, options);
  }

  emit(event, data = {}, callback) {
    if (!this.socket?.connected) {
      console.log('[newsocketService] emit skipped because socket is not connected', {
        event,
        payload: data,
      });
      return false;
    }

    logSocketEvent('emit', event, data);
    this.socket.emit(event, data, callback);
    return true;
  }

  emitWithAck(event, data = {}) {
    return new Promise((resolve, reject) => {
      if (!this.socket?.connected) {
        console.log('[newsocketService] emitWithAck skipped because socket is not connected', {
          event,
          payload: data,
        });
        reject(new Error('Socket not connected'));
        return;
      }

      logSocketEvent('emitWithAck', event, data);
      this.socket.timeout(20000).emit(event, data, (err, response) => {
        if (err) {
          console.log('[newsocketService] emitWithAck error', {
            event,
            payload: data,
            error: err,
          });
          reject(err);
          return;
        }

        console.log('[newsocketService] emitWithAck response', {
          event,
          payload: data,
          response,
        });
        resolve(response);
      });
    });
  }

  on(event, callback) {
    if (!this.globalListeners.has(event)) {
      this.globalListeners.set(event, new Set());
    }

    const callbacks = this.globalListeners.get(event);
    callbacks.add(callback);

    console.log('[newsocketService] listener added', {
      event,
      listenerCount: callbacks.size,
    });

    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  off(event, callback) {
    if (!this.globalListeners.has(event)) {
      return;
    }

    if (callback) {
      const callbacks = this.globalListeners.get(event);
      callbacks.delete(callback);
      console.log('[newsocketService] listener removed', {
        event,
        listenerCount: callbacks.size,
      });
      if (this.socket) {
        this.socket.off(event, callback);
      }

      if (callbacks.size === 0) {
        this.globalListeners.delete(event);
      }
      return;
    }

    this.globalListeners.delete(event);
    console.log('[newsocketService] listeners cleared for event', {
      event,
    });

    if (this.socket) {
      this.socket.off(event);
    }
  }

  removeAllListeners(event) {
    if (this.socket) {
      this.socket.removeAllListeners(event);
    }

    if (event) {
      this.globalListeners.delete(event);
      return;
    }

    this.globalListeners.clear();
  }

  disconnect() {
    if (this.socket) {
      console.log('[newsocketService] disconnect called', {
        socketId: this.socket.id,
        connected: this.socket.connected,
      });
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.initializedSocket = false;
      this.connectingPromise = null;
    }
  }

  close() {
    this.disconnect();
  }

  isConnected() {
    return this.socket?.connected || false;
  }

  sendWellbeingOtp(data = {}, callback) {
    return this.emit('sendwellbeingotp', data, callback);
  }

  verifyWellbeingOtp(data = {}, callback) {
    return this.emit('verifywellbeingotp', data, callback);
  }

  requestWellbeingCheck(data = {}, callback) {
    return this.emit('requestwellbeingcheck', data, callback);
  }

  wellbeingEscort(data = {}, callback) {
    return this.emit('wellbeingescort', data, callback);
  }

  sendPushNotification(data = {}, callback) {
    return this.emit('sendpushnotification', data, callback);
  }

  endWellbeingCheck(data = {}, callback) {
    return this.emit('wellbeingcheckend', data, callback);
  }

  virtualTravelGuard(data = {}, callback) {
    return this.emit('virtualtravelguard', data, callback);
  }

  connectSocket(onConnect, onDisconnect, onMessage, onUserLiveLocation) {
    if (typeof onConnect === 'function') {
      this.on('connect', onConnect);
    }

    if (typeof onDisconnect === 'function') {
      this.on('disconnect', onDisconnect);
    }

    if (typeof onMessage === 'function') {
      this.on('message', onMessage);
    }

    if (typeof onUserLiveLocation === 'function') {
      this.on('userLiveLocation', onUserLiveLocation);
    }

    return this.initializeSocket();
  }

  disconnectSocket() {
    this.disconnect();
  }

  sendMessage(message) {
    return this.emit('message', message);
  }

  userLiveLocation(live) {
    return this.emit('userLiveLocation', live);
  }
}

const socketService = new SocketService();

export const connectSocket = (...args) => socketService.connectSocket(...args);
export const disconnectSocket = () => socketService.disconnectSocket();
export const sendMessage = message => socketService.sendMessage(message);
export const userLiveLocation = live => socketService.userLiveLocation(live);

export default socketService;
